import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Database,
  Gauge,
  Globe,
  Package,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { panel } from '@/components/surface/tones';
import { IconTile, Skeleton } from '@/components/surface/ui';
import { AUTOMATION_CATALOG_KEY, automationApi } from '@/lib/automation-api';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { relativeTime } from '@/modules/monitoring/format';
import { serverQuery } from '@/modules/automation/parameters';
import { categoryLabel, categoryOrder } from '@/modules/automation/status';
import { PageHeader, RunStatusPill } from '@/modules/automation/ui';
import type { PlaybookSummary } from '@/types/api';

const CATEGORY_ICON: Record<PlaybookSummary['category'], LucideIcon> = {
  services: RefreshCw,
  packages: Package,
  maintenance: Wrench,
  runtimes: Box,
  web: Globe,
  databases: Database,
  monitoring: Gauge,
  security: ShieldCheck,
};

/**
 * Every playbook, by what it is for.
 *
 * A playbook is a tested procedure the platform carries out the same way on every supported
 * distribution. Choosing one opens its form: what it will do, the few choices it takes, which servers,
 * and how carefully.
 */
export function PlaybooksPage() {
  const catalog = useQuery({ queryKey: AUTOMATION_CATALOG_KEY, queryFn: () => automationApi.catalog(), staleTime: 60_000 });
  // Opened from a server's page: the server stays chosen on whichever playbook is picked.
  const [searchParams] = useSearchParams();
  const carry = serverQuery(searchParams);

  const byCategory = categoryOrder
    .map((category) => ({
      category,
      playbooks: (catalog.data?.playbooks ?? []).filter((playbook) => playbook.category === category),
    }))
    .filter((group) => group.playbooks.length > 0);

  return (
    <div className="space-y-8">
      <PageHeader
        title="Playbooks"
        description="Tested procedures that detect each server's distribution, package manager and privileges, and do the same job correctly on all of them. Every playbook can be dry-run first, and every run is recorded."
      />

      {catalog.isLoading && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 min-[1800px]:grid-cols-4">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-40" />
          ))}
        </div>
      )}

      {catalog.isError && <p className="text-sm text-danger">The playbooks could not be loaded: {getErrorMessage(catalog.error)}</p>}

      {byCategory.map(({ category, playbooks }) => (
        <section key={category} aria-labelledby={`category-${category}`} className="space-y-3">
          <h2 id={`category-${category}`} className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">
            {categoryLabel[category]}
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 min-[1800px]:grid-cols-4">
            {playbooks.map((playbook) => (
              <PlaybookCard key={playbook.key} playbook={playbook} carry={carry} />
            ))}
          </div>
        </section>
      ))}

      {catalog.isSuccess && (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Sparkles className="size-3.5" aria-hidden />
          Need several at once? <Link to="/automation/templates" className="text-primary hover:underline">Templates</Link> run a
          tested sequence of playbooks to prepare a server for a purpose.
        </p>
      )}
    </div>
  );
}

function PlaybookCard({ playbook, carry }: { playbook: PlaybookSummary; carry: string }) {
  return (
    <Link
      to={`/automation/playbooks/${playbook.key}${carry}`}
      className={cn(
        panel,
        'group flex flex-col gap-3 p-5 transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:-translate-y-0.5',
      )}
    >
      <div className="flex items-start gap-3">
        <IconTile icon={CATEGORY_ICON[playbook.category]} tone="primary" />
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-semibold leading-6 tracking-tight group-hover:text-primary">{playbook.name}</h3>
          <p className="text-[13px] text-muted-foreground">{playbook.summary}</p>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">{playbook.supported_systems}</p>

      <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-slate-900/[0.07] pt-3 text-xs dark:border-white/[0.07]">
        {playbook.last_run !== null ? (
          <span className="flex min-w-0 items-center gap-2">
            <RunStatusPill status={playbook.last_run.status} label={playbook.last_run.status_label} />
            <span className="truncate text-muted-foreground">{relativeTime(playbook.last_run.created_at)}</span>
          </span>
        ) : (
          <span className="text-muted-foreground">Not run yet</span>
        )}
        <span className="font-medium text-primary">Open →</span>
      </div>
    </Link>
  );
}
